import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { apiFetch } from "../api/client";
import { ga4, type TrackProduct } from "../analytics/ga4";

/* Adatta un CartItem al formato prodotto per GA4 (item_id = slug, fallback UUID). */
function toTrack(i: { slug?: string; id: string; title: string; price_cents: number }): TrackProduct {
  return { item_id: i.slug || i.id, item_name: i.title, price_cents: i.price_cents };
}

export interface CartSubItem {
  title: string;
  price_cents: number;
  image?: string;
}

export interface CartItem {
  id: string;
  title: string;
  slug: string;
  price_cents: number;
  currency: string;
  quantity: number;
  image?: string;
  subItems?: CartSubItem[];
}

/* ── Quote del carrello: calcolato dal backend (MOQ + sconti commerciali) ── */
export interface CartQuoteLine {
  product_id: string;
  title: string;
  quantity: number;
  unit_price_cents: number;
  moq: number | null;
  moq_met: boolean;
  qty_discount_pct: number;
  advance_discount_pct: number;
  line_discount_pct: number;
  line_subtotal_cents: number;
  line_discount_cents: number;
  line_total_cents: number;
}

export interface CartQuote {
  lines: CartQuoteLine[];
  advance_payment: boolean;
  subtotal_cents: number;
  total_discount_cents: number;
  total_cents: number;            // NETTO (imponibile) — retrocompat
  shipping_cents: number;
  shipping_included: boolean;
  prices_vat_excluded: boolean;
  // ── Ripartizione IVA (aliquota unica piatta). vat_rate_percent 0 = IVA non applicata ──
  taxable_cents: number;          // imponibile netto (dopo sconti)
  vat_rate_percent: number;       // es. 7
  vat_cents: number;              // importo IVA
  total_gross_cents: number;      // TOTALE IVA inclusa (da mostrare come totale)
  can_checkout: boolean;
  errors: string[];
}

interface CartState {
  items: CartItem[];
  isOpen: boolean;
  totalItems: number;
  totalCents: number;
  advancePayment: boolean;
  quote: CartQuote | null;
  addItem: (product: Omit<CartItem, "quantity">, quantity?: number) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, qty: number) => void;
  clearCart: () => void;
  toggleCart: () => void;
  setOpen: (open: boolean) => void;
}

const CartContext = createContext<CartState | null>(null);

const STORAGE_KEY = "gb_cart";

/**
 * Standard di vendita Granborgo: ogni prodotto si acquista in confezioni da
 * almeno 36 pezzi. Aggiungendo un prodotto al carrello (carrello vuoto o nuova
 * riga) la quantità parte automaticamente da questo minimo. Fonte unica di
 * verità: importare MIN_PACK_QTY invece di ripetere il numero 36 altrove.
 */
export const MIN_PACK_QTY = 36;

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(loadCart);
  const [isOpen, setOpen] = useState(false);
  // O1 — pagamento anticipato −2%: sempre attivo, non più opzionale (nessuna checkbox).
  const advancePayment = true;
  const [quote, setQuote] = useState<CartQuote | null>(null);
  // Stato carrello sempre aggiornato per gli eventi GA4 (evita di leggere valori
  // stale dentro le callback e di emettere eventi dal reducer, che in StrictMode
  // gira due volte). openedByAdd distingue l'apertura automatica dopo "aggiungi"
  // (già tracciata da add_to_cart) da un'apertura volontaria del carrello.
  const itemsRef = useRef<CartItem[]>(items);
  const openedByAddRef = useRef(false);

  useEffect(() => {
    saveCart(items);
    itemsRef.current = items;
  }, [items]);

  // view_cart: solo su apertura volontaria del carrello (non dopo un add_to_cart).
  useEffect(() => {
    if (!isOpen) { openedByAddRef.current = false; return; }
    if (openedByAddRef.current) { openedByAddRef.current = false; return; }
    const its = itemsRef.current;
    if (its.length === 0) return;
    ga4.viewCart(
      its.map((i) => ({ item_id: i.slug || i.id, item_name: i.title, price: i.price_cents / 100, quantity: i.quantity })),
      its.reduce((s, i) => s + i.price_cents * i.quantity, 0),
      its[0]?.currency || "EUR",
    );
  }, [isOpen]);

  // Clear cart when user logs out
  useEffect(() => {
    const handleLogout = () => setItems([]);
    window.addEventListener("gb:logout", handleLogout);
    return () => window.removeEventListener("gb:logout", handleLogout);
  }, []);

  // Quote dal backend: applica MOQ + sconti commerciali. Il frontend NON calcola.
  useEffect(() => {
    if (items.length === 0) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      apiFetch<CartQuote>("/api/v1/shop/cart/quote", {
        method: "POST",
        body: JSON.stringify({
          items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
          advance_payment: advancePayment,
        }),
      })
        .then((q) => { if (!cancelled) setQuote(q); })
        .catch(() => { /* rete ko: il carrello resta usabile, niente breakdown */ });
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [items, advancePayment]);

  const addItem = useCallback((product: Omit<CartItem, "quantity">, quantity?: number) => {
    // GA4 add_to_cart: quantità EFFETTIVAMENTE aggiunta (delta), calcolata fuori
    // dal reducer usando lo stato fresco via ref.
    const existing = itemsRef.current.find((i) => i.id === product.id);
    const addedQty = existing ? (quantity ?? 1) : (quantity ?? MIN_PACK_QTY);
    ga4.addToCart(toTrack(product), addedQty, product.currency);
    openedByAddRef.current = true;
    setItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        // Riga già presente: incrementa. I click "aggiungi" dalle card fanno +1;
        // ConfezioneDetail passa la quantità scelta dall'utente.
        const add = quantity ?? 1;
        return prev.map((i) => (i.id === product.id ? { ...i, ...product, quantity: i.quantity + add } : i));
      }
      // Nuova riga: se il chiamante passa una quantità esplicita la rispetta
      // (ConfezioneDetail passa la sua, già >= al MOQ del prodotto; i bundle
      // passano 1). Altrimenti gli "aggiungi" dalle card — che non hanno un
      // selettore quantità — partono dal default confezione (36 pz).
      const initialQty = quantity ?? MIN_PACK_QTY;
      return [...prev, { ...product, quantity: initialQty }];
    });
    setOpen(true);
  }, []);

  const removeItem = useCallback((id: string) => {
    const item = itemsRef.current.find((i) => i.id === id);
    if (item) ga4.removeFromCart(toTrack(item), item.quantity, item.currency);
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateQuantity = useCallback((id: string, qty: number) => {
    // GA4: una diminuzione = remove_from_cart(delta), un aumento = add_to_cart(delta).
    const item = itemsRef.current.find((i) => i.id === id);
    if (item) {
      if (qty <= 0) ga4.removeFromCart(toTrack(item), item.quantity, item.currency);
      else if (qty < item.quantity) ga4.removeFromCart(toTrack(item), item.quantity - qty, item.currency);
      else if (qty > item.quantity) ga4.addToCart(toTrack(item), qty - item.quantity, item.currency);
    }
    if (qty <= 0) {
      setItems((prev) => prev.filter((i) => i.id !== id));
    } else {
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, quantity: qty } : i)));
    }
  }, []);

  const clearCart = useCallback(() => setItems([]), []);
  const toggleCart = useCallback(() => setOpen((o) => !o), []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalCents = items.reduce((sum, i) => sum + i.price_cents * i.quantity, 0);

  const value = useMemo(
    () => ({
      items, isOpen, totalItems, totalCents, advancePayment, quote,
      addItem, removeItem, updateQuantity, clearCart, toggleCart, setOpen,
    }),
    [items, isOpen, totalItems, totalCents, advancePayment, quote,
      addItem, removeItem, updateQuantity, clearCart, toggleCart],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartState {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
